import { Auth } from "aws-amplify";
import { UserType } from "../../../graphql/types";
import enumStrings, { RedirectUri } from "../../enumStrings";
import {
  clearPendingNewPasswordChallenge,
  setPendingNewPasswordChallenge,
} from "./pendingNewPasswordChallenge";
import { getSingleCognitoUserTypeFromGroups } from "../../common/utilities/adminConsoleUserType";

const getApiUri = () =>
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_PROD_API_URL
    : process.env.REACT_APP_DEV_API_URL;

/**
 * Monday M5: the chamber of a member login, read from CAP with the new access
 * token. Null when CAP cannot answer (the login then continues as before).
 */
export const getSignedInMemberChamberId = async (
  user: { signInUserSession?: { accessToken?: { jwtToken?: string; payload?: { sub?: string } } } },
  fetchImpl: typeof fetch = fetch
): Promise<string | null> => {
  const token = user.signInUserSession?.accessToken?.jwtToken;
  const userId = user.signInUserSession?.accessToken?.payload?.sub;
  const uri = getApiUri();
  if (!token || !userId || !uri) return null;
  try {
    const response = await fetchImpl(uri, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify({
        query: "query MemberLoginChamber($userId: String!) { getUser(userId: $userId) { chamberId } }",
        variables: { userId },
      }),
    });
    const result = await response.json();
    const chamberId = result?.data?.getUser?.chamberId;
    return typeof chamberId === "string" && chamberId ? chamberId : null;
  } catch {
    return null;
  }
};

/**
 * Switch chamber (M1/M3/M5): makes the chamber of this login page active for
 * the new session. True: home or linked chamber. False: not this login's
 * chamber. Null: CAP cannot answer (older API or network).
 */
export const selectSignedInMemberChamber = async (
  user: { signInUserSession?: { accessToken?: { jwtToken?: string } } },
  chamberId: string,
  fetchImpl: typeof fetch = fetch
): Promise<boolean | null> => {
  const token = user.signInUserSession?.accessToken?.jwtToken;
  const uri = getApiUri();
  if (!token || !uri) return null;
  try {
    const response = await fetchImpl(uri, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify({
        query: "mutation SelectMemberLoginChamber($chamberId: String!) { selectMemberDashboardChamber(chamberId: $chamberId) { selected } }",
        variables: { chamberId },
      }),
    });
    const result = await response.json();
    const selected = result?.data?.selectMemberDashboardChamber?.selected;
    return typeof selected === "boolean" ? selected : null;
  } catch {
    return null;
  }
};

export const signInHandler = async (
  email: string,
  password: string,
  onNewPasswordRequired?: () => void,
  /** Branded member login: refuse a login that belongs to another chamber. */
  expectedChamberId?: string
): Promise<void> => {
  try {
    clearPendingNewPasswordChallenge();
    sessionStorage.removeItem(
      enumStrings.sessionStorageKey.loggedInWithTemporaryPassword
    );
    const user = await Auth.signIn(email, password);

    if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
      if (!onNewPasswordRequired) {
        await Auth.signOut();
        const challengeError = new Error(
          "A new password must be set interactively."
        ) as Error & { code?: string };
        challengeError.code = "NewPasswordChallengeRequiresInteractiveLogin";
        throw challengeError;
      }

      setPendingNewPasswordChallenge({
        email,
        user,
      });
      onNewPasswordRequired();
      return;
    }

    if (
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.payload &&
      user.signInUserSession.idToken.payload["custom:isDisabled"] === "true"
    ) {
      await Auth.signOut();

      const disabledError = new Error("This account is disabled.") as Error & {
        code?: string;
      };
      disabledError.code = "UserDisabled";
      throw disabledError;
    }

    const signedInUserType = getSingleCognitoUserTypeFromGroups(
      user.signInUserSession?.accessToken?.payload?.["cognito:groups"]
    );
    if (!signedInUserType || signedInUserType === UserType.CapUser) {
      await Auth.signOut();
      const accessError = new Error(
        "This account cannot access this dashboard."
      ) as Error & { code?: string };
      accessError.code =
        signedInUserType === UserType.CapUser
          ? "CapUserTryingToSignIn"
          : "UserDisabled";
      throw accessError;
    }

    if (expectedChamberId && signedInUserType === UserType.ChamberMemberAdmin) {
      const selected = await selectSignedInMemberChamber(user, expectedChamberId);
      const chamberId = selected === null ? await getSignedInMemberChamberId(user) : null;
      if (selected === false || (chamberId && chamberId !== expectedChamberId)) {
        await Auth.signOut();
        const chamberError = new Error(
          "This login belongs to another chamber."
        ) as Error & { code?: string };
        chamberError.code = "WrongChamber";
        throw chamberError;
      }
    }

    window.location.href = RedirectUri.home;
  } catch (error) {
    throw error;
  }
};
